import React from 'react';

import { snakeCase, some } from 'lodash';
import { AppContext } from '../contexts/app_context';

// NOTE: check-time snake_case translation for restaurant permissions so
// call sites can pass `['jobCreate']` regardless of API casing. Chain flags
// stay camelCase (caller writes `['periodJobCreate']`, we look up directly).
const useCheckPermissions = (
  permissions: string[],
  options?: { restaurantId?: string },
): { isAllowed: boolean; isRejected: boolean } => {
  const { contextPermissions, contextCurrentRestaurant } = React.useContext(AppContext);

  const restaurantId = options?.restaurantId ?? contextCurrentRestaurant?.id ?? null;
  const chainPermissions = contextPermissions?.chain ?? {};
  const legacyRestaurantPermissions = contextPermissions?.restaurant ?? {};
  const restaurantList = restaurantId ? contextPermissions?.restaurants?.[restaurantId] : null;

  const isAllowed = some(permissions, (perm) => {
    if (chainPermissions[perm as keyof typeof chainPermissions] === true) {
      return true;
    }

    const nameMatch = !!restaurantList && some(restaurantList, (name) => snakeCase(name) === snakeCase(perm));
    if (nameMatch) {
      return true;
    }

    // last-resort fallback for the legacy single-restaurant `restaurant.jobCreate` field.
    return perm === 'jobCreate' && legacyRestaurantPermissions.jobCreate === true;
  });

  return { isAllowed, isRejected: !isAllowed };
};

export default useCheckPermissions;
